import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';
import AssetsRedesign from './AssetsRedesign';

interface Asset {
  id: string;
  assetName: string;
  assetType: string;
  eicCode: string;
  eicWCode?: string | null;
  installedCapacityMw: number;
  region: string;
  operationalStatus: string;
  counterpartyName: string;
  commissioningDate: string;
}

const ASSET_TYPE_ICONS: Record<string, string> = {
  solar: 'ri-sun-line',
  wind: 'ri-windy-line',
  bess: 'ri-battery-2-charge-line',
  hybrid: 'ri-flashlight-line',
  consumer_site: 'ri-building-line',
};

/**
 * Дуальний режим (§6 handoff): стенд із VITE_REDESIGN бачить редизайн, прод —
 * незмінений AssetsPageLegacy нижче. Маршрут /assets не має feature-гейта,
 * тож у прод-збірці ця сторінка присутня і ламати її не можна.
 */
export default function AssetsPage() {
  return import.meta.env.VITE_REDESIGN === '1' ? <AssetsRedesign /> : <AssetsPageLegacy />;
}

function AssetsPageLegacy() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [items, setItems] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const selectBg = isDark ? '#1A1A1A' : '#fff';

  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const data = await api.get<Asset[]>('/assets');
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setItems([]);
      } else {
        const message = getApiErrorMessage(err, 'Failed to load assets');
        addToast(message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter((a) => {
    const matchesSearch = a.assetName.toLowerCase().includes(search.toLowerCase()) || (a.eicCode.includes(search) || (a.eicWCode || '').includes(search));
    const matchesType = !typeFilter || a.assetType === typeFilter;
    return matchesSearch && matchesType;
  });

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      planned: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
      construction: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      commissioning: { bg: 'rgba(31,91,143,0.1)', color: '#1F5B8F' },
      suspended: { bg: 'color-mix(in srgb, var(--warn) 12%, transparent)', color: 'var(--warn)' },
      decommissioned: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
    };
    const style = map[status] ?? map.planned;
    return (
      <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize" style={{ background: style.bg, color: style.color }}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  return (
    <DashboardLayout breadcrumb={[t('assets_title')]}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
            {t('assets_title')}
          </h1>
          <p className="text-sm mt-1" style={{ color: textMuted }}>
            {t('assets_subtitle')}
          </p>
        </div>
      </div>

      <div className="rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center gap-3 p-4 flex-wrap" style={{ borderBottom: `1px solid ${separator}` }}>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 min-w-[200px] max-w-md" style={{ background: inputBg, border: `1px solid ${inputBorder}` }}>
            <i className="ri-search-line text-sm" style={{ color: textMuted }}></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('assets_search')}
              className="bg-transparent outline-none text-sm flex-1"
              style={{ color: textMain }}
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 rounded-md text-sm border outline-none cursor-pointer"
            style={{ borderColor: inputBorder, background: selectBg, color: textMain }}
          >
            <option value="">{t('assets_all_types')}</option>
            <option value="solar">Solar</option>
            <option value="wind">Wind</option>
            <option value="bess">BESS</option>
            <option value="hybrid">Hybrid</option>
            <option value="consumer_site">Consumer Site</option>
          </select>
          <button
            onClick={loadAssets}
            className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors"
            style={{ border: `1px solid ${inputBorder}`, background: selectBg }}
            title="Refresh"
          >
            <i className="ri-refresh-line text-sm" style={{ color: textMuted }}></i>
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: textMuted }}></i>
            <p className="text-sm mt-3" style={{ color: textMuted }}>{t('assets_loading')}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
              <i className="ri-flashlight-line text-xl" style={{ color: textMuted }}></i>
            </div>
            <p className="text-sm font-medium" style={{ color: textMain }}>{t('assets_not_found')}</p>
            <p className="text-xs mt-1" style={{ color: textMuted }}>
              {search || typeFilter ? t('assets_filter_hint') : t('assets_not_found_hint')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: tableHeaderBg }}>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('assets_col_asset')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('assets_col_eic')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('assets_col_type')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('assets_col_capacity')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('assets_col_region')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('assets_col_status')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('assets_col_counterparty')}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((asset) => (
                  <tr
                    key={asset.id}
                    className="cursor-pointer transition-colors"
                    onClick={() => navigate(`/assets/${asset.id}`)}
                    style={{ borderBottom: `1px solid ${tableBorder}` }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: iconBg }}>
                          <i className={`${ASSET_TYPE_ICONS[asset.assetType] ?? 'ri-flashlight-line'} text-sm`} style={{ color: 'var(--accent)' }}></i>
                        </div>
                        <div className="font-medium" style={{ color: textMain }}>{asset.assetName}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: textMuted }}>
                      <div>{asset.eicCode}</div>
                      {asset.eicWCode && <div className="text-[10px]" style={{ color: '#9B9BA4' }}>W: {asset.eicWCode}</div>}
                    </td>
                    <td className="px-4 py-3 capitalize" style={{ color: textMuted }}>{asset.assetType.replace('_', ' ')}</td>
                    <td className="px-4 py-3 font-medium" style={{ color: textMain }}>{asset.installedCapacityMw} МВт</td>
                    <td className="px-4 py-3" style={{ color: textMuted }}>{asset.region}</td>
                    <td className="px-4 py-3">{getStatusBadge(asset.operationalStatus)}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: textMuted }}>{asset.counterpartyName}</td>
                    <td className="px-4 py-3">
                      <button className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer transition-colors"
                        onMouseEnter={(e) => { e.currentTarget.style.background = iconBg; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <i className="ri-arrow-right-s-line" style={{ color: textMuted }}></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}